import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'

/**
 * Constants for ei-input-field component
 */
export const EI_INPUT_FIELD_CONSTANTS = {
    /**
     * Keyboard keys that should be allowed in phone input fields
     * Includes navigation, control, and editing keys
     */
    ALLOWED_PHONE_KEYS: [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
    ] as const,

    /**
     * Keyboard modifier keys for shortcuts
     */
    KEYBOARD_MODIFIERS: {
        CTRL: 'ctrlKey',
        META: 'metaKey',
    } as const,

    /**
     * Keyboard shortcuts for phone input
     */
    KEYBOARD_SHORTCUTS: {
        SELECT_ALL: 'a',
        COPY: 'c',
        PASTE: 'v',
        CUT: 'x',
        UNDO: 'z',
    } as const,
} as const

const navigationKeysForRestrictedInput =
    EI_INPUT_FIELD_CONSTANTS.ALLOWED_PHONE_KEYS as readonly string[]
const modifierShortcutKeys = Object.values(
    EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS,
) as readonly string[]

/** Allowed in contact first/last name (matches REGEX_PATTERNS.CONTACT_BOOK_PERSON_NAME). */
export function sanitizeContactPersonNameFragment(raw: string): string {
    return raw.replace(REGEX_PATTERNS.CONTACT_BOOK_PERSON_NAME_DISALLOWED_CHARS_GLOBAL, '')
}

export function shouldAllowContactPersonNameKeyDown(event: KeyboardEvent): boolean {
    const key = event.key

    if (navigationKeysForRestrictedInput.includes(key)) {
        return true
    }

    if ((event.ctrlKey || event.metaKey) && modifierShortcutKeys.includes(key.toLowerCase())) {
        return true
    }

    if (key === "'" || key === '-' || key === ' ') {
        return true
    }

    if (key.length === 1 && /[a-zA-Z]/.test(key)) {
        return true
    }

    return false
}

/**
 * Strips non-digits and normalizes leading zeros (shared with OMS student-count flows).
 */
export function sanitizeToPositiveIntegerString(raw: string): string {
    const digitsOnly = raw.replace(REGEX_PATTERNS.ORDER_ID_NON_NUMERIC, '')
    if (digitsOnly === '') {
        return ''
    }
    const stripped = digitsOnly.replace(REGEX_PATTERNS.ORDER_ID_LEADING_ZEROS, '')
    return stripped === '' ? '0' : stripped
}

/**
 * Returns true when the key should be accepted for a digits-only non-negative integer field.
 */
export function shouldAllowPositiveIntegerKeyDown(event: KeyboardEvent): boolean {
    const key = event.key

    if (navigationKeysForRestrictedInput.includes(key)) {
        return true
    }

    if ((event.ctrlKey || event.metaKey) && modifierShortcutKeys.includes(key.toLowerCase())) {
        return true
    }

    return REGEX_PATTERNS.KEY_EVENT_SINGLE_ASCII_DIGIT.test(key)
}
